/**
 * RYOXZEL scroll reveal.
 *
 * Plays one light fade-and-rise entrance the first time an element marked with
 * `[data-ryoxzel-reveal]` enters the viewport, then stops observing it. Nothing
 * animates on page load, on scrolling back up, or after a Theme Editor rerender
 * finishes — a section plays once per page view.
 *
 * The script only adds classes; the resting CSS state stays visible, so a
 * failed or late load cannot hide content. Options mirror the approved
 * reference: threshold 0.04, bottom inset -40px, ~0.7s opacity + transform.
 *
 * See docs/RYOXZEL_UI_LIBRARY.md — "Page and section entrances".
 */

const TARGET_SELECTOR = '[data-ryoxzel-reveal]';
const READY_CLASS = 'ryoxzel-reveal--ready';
const VISIBLE_CLASS = 'ryoxzel-reveal--visible';

/** Bottom inset for the observer, so a section plays just inside the fold. */
const REVEAL_INSET = 40;

/** @type {IntersectionObserverInit} */
const OBSERVER_OPTIONS = {
  threshold: 0.04,
  rootMargin: `0px 0px -${REVEAL_INSET}px 0px`,
};

/** Elements waiting to be revealed. @type {Set<Element>} */
const pending = new Set();

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | null} */
let observer = null;

function getObserver() {
  observer ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) reveal(entry.target);
    }
  }, OBSERVER_OPTIONS);

  return observer;
}

function stopObserving(element) {
  pending.delete(element);
  observer?.unobserve(element);
}

/**
 * Reveals an element. When the ready state has not been applied yet, the hidden
 * and released states land in the same task, so the element never paints hidden.
 */
function reveal(element) {
  element.classList.add(READY_CLASS, VISIBLE_CLASS);
  stopObserving(element);
}

/**
 * True once an element's top edge is at or above the fold. Used for the first
 * paint, restored scroll positions, and Theme Editor scroll jumps, where an
 * entrance would read as a blink rather than a scroll effect.
 */
function isAtOrAboveFold(element) {
  return element.getBoundingClientRect().top <= window.innerHeight;
}

function initElement(element) {
  if (!(element instanceof HTMLElement) || pending.has(element)) return;
  if (element.classList.contains(VISIBLE_CLASS)) return;

  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    element.classList.remove(READY_CLASS);
    return;
  }

  if (isAtOrAboveFold(element)) {
    reveal(element);
    return;
  }

  element.classList.add(READY_CLASS);
  pending.add(element);
  getObserver().observe(element);
}

function findTargets(root) {
  if (!(root instanceof Element || root instanceof Document)) return [];

  const targets = [...root.querySelectorAll(TARGET_SELECTOR)];
  if (root instanceof Element && root.matches(TARGET_SELECTOR)) targets.unshift(root);

  return targets;
}

function initWithin(root) {
  for (const element of findTargets(root)) initElement(element);
}

/** Reveals anything the fold has already passed (late scroll restore). */
function reconcile() {
  for (const element of [...pending]) {
    if (isAtOrAboveFold(element)) reveal(element);
  }
}

/** Reduced motion turns on mid-session: settle everything without animating. */
function revealAllPending() {
  for (const element of [...pending]) reveal(element);
}

function teardown(root) {
  if (!(root instanceof Element)) return;

  for (const element of [...pending]) {
    if (root.contains(element)) stopObserving(element);
  }
}

initWithin(document);

window.addEventListener('load', reconcile, { once: true });

document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
document.addEventListener('shopify:section:unload', (event) => teardown(event.target));

reducedMotion.addEventListener('change', (event) => {
  if (event.matches) revealAllPending();
});
